import clsx from 'clsx'
import React, { CSSProperties, FC, MouseEvent, MouseEventHandler, useCallback, useMemo } from 'react'

import SvgAngleRight from '@Assets/SVG/AngleRight'
import { ImageV3 } from '@Components/Image'
import { Link } from '@Components/Link'
import { getMenuItemTitle } from '@Domains/Navigation/Features/Menu/helpers'
import { MainMenuActionConfig, MenuItem, MenuItemChild } from '@Domains/Navigation/Features/Menu/Menu'
import { useTranslation } from '@Globals/Translations'

import styles from './MenuElement.module.scss'

const menuItemTitle = (menuItem: MenuItem | MenuItemChild) => {
  const title = getMenuItemTitle(menuItem)
  if (!menuItem.titleMobile || menuItem.titleMobile === title) {
    return title
  }

  return (
    <>
      <span className={styles.titleDesktop}>{title}</span>
      <span className={styles.titleMobile}>{menuItem.titleMobile}</span>
    </>
  )
}

type MenuAction = (
  event: MouseEvent<HTMLElement>,
  item: MenuItem | MenuItemChild,
  config?: MainMenuActionConfig) => void

type MenuElementProps = {
  className?: string
  isRootCategory?: boolean
  menuItem: MenuItem | MenuItemChild
  onMouseLeave?: MouseEventHandler<HTMLAnchorElement | HTMLDivElement> | undefined
  shown: boolean
  triggerMenu: MenuAction
  viewAllElement?: boolean
}
let timeoutId

export const MenuElement: FC<MenuElementProps> = ({
  className,
  isRootCategory,
  menuItem,
  onMouseLeave,
  shown,
  triggerMenu,
  viewAllElement,
}) => {
  const { t } = useTranslation()

  const handleMouseEnter = useCallback((callback) => {
    timeoutId = setTimeout(() => {
      callback()
    }, 200)
  }, [])

  const handleMouseLeave = useCallback(() => {
    clearTimeout(timeoutId)
  }, [])
  const content = useMemo(() => (
    <>
      {viewAllElement && <h2>{menuItemTitle(menuItem)}</h2>}
      {menuItem.link?.image?.url
        ? (shown && <ImageV3 alt={menuItem.title || ''} height={40} loading="eager" priority quality={100} src={menuItem.link?.image.url || ''} width={40} />)
        : <span />}
      <p>
        {viewAllElement && t('header:HEADER_MOBILE_MENU_VIEW_ALL_LABEL')}
        {!viewAllElement && menuItemTitle(menuItem)}
      </p>
      {!menuItem.features?.includes('discover_more') && <SvgAngleRight />}
    </>
  ), [menuItem, shown, t, viewAllElement])

  if (menuItem.features?.includes('is_hidden')) {
    return null
  }

  const styleProps = {
    '--background-color-desktop': menuItem?.appearanceDesktop?.backgroundColor,
    '--text-color-desktop': menuItem?.appearanceDesktop?.textColor,
    '--background-color-mobile': menuItem?.appearanceMobile?.backgroundColor,
    '--text-color-mobile': menuItem?.appearanceMobile?.textColor,
  } as CSSProperties

  if (menuItem.link?.url && !menuItem.features?.includes('discover_more')) {
    return (
      <Link
        className={clsx(className, 'anchor')}
        href={menuItem.link?.url}
        onClick={e => triggerMenu(e, menuItem, {
          isRootCategory,
          viewAllElement,
          isClickEvent: true,
        })}
        onMouseEnter={e => handleMouseEnter(() => {
          triggerMenu(e, menuItem, {
            isRootCategory,
            checkDesktop: true,
            viewAllElement,
          })
        })}
        onMouseLeave={(e) => {
          if (onMouseLeave) {
            onMouseLeave(e)
          }
          handleMouseLeave()
        }}
        style={styleProps}
      >
        {content}
      </Link>
    )
  }

  return (
    <div
      className={className}
      onClick={(e) => {
        if (!menuItem.features?.includes('discover_more')) {
          triggerMenu(e, menuItem, {
            isRootCategory,
            viewAllElement,
            isClickEvent: true,
          })
        }
      }}
      onMouseEnter={e => handleMouseEnter(() => {
        triggerMenu(e, menuItem, {
          isRootCategory,
          checkDesktop: true,
          viewAllElement,
        })
      })}
      onMouseLeave={(e) => {
        if (onMouseLeave) {
          onMouseLeave(e)
        }
        handleMouseLeave()
      }}
      style={styleProps}
    >
      {content}
    </div>
  )
}
